import React, { useState, useMemo } from "react";
import {
  Box, TextField, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Typography, Chip,
  Autocomplete, IconButton, Menu, MenuItem,
} from "@mui/material";
import { Plus, Kanban, MoreHorizontal, Users } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { projectService } from "../../api/services/projectService";
import { identityService } from "../../api/services/identityService";
import { PageHeader } from "../../components/Common";
import DataTable from "../../components/DataTable";
import { rememberProject, projectViewPath } from "../../utils/ticketProjectNav";

export default function ProjectsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [detailProject, setDetailProject] = useState(null);

  const openBoard = (project) => {
    rememberProject(project.id);
    navigate(projectViewPath(project.id, "board"));
  };

  const { data: projects, isLoading } = useQuery({ queryKey: ["projects"], queryFn: projectService.list });

  const createMutation = useMutation({
    mutationFn: () => projectService.create({
      name: form.name,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      member_employee_ids: [],
    }),
    onSuccess: () => {
      setCreateOpen(false);
      setForm({ name: "", start_date: "", end_date: "" });
      enqueueSnackbar("Project created", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const columns = [
    { key: "name", label: "Project", render: (p) => <strong>{p.name}</strong> },
    { key: "status", label: "Status", render: (p) => <Chip size="small" label={p.status} sx={{ textTransform: "capitalize" }} /> },
    { key: "ticket_count", label: "Tickets" },
    {
      key: "completion_percent", label: "Completion",
      render: (p) => (
        <Box sx={{ width: 140 }}>
          <LinearProgress variant="determinate" value={p.completion_percent} sx={{ height: 6, borderRadius: 3, mb: 0.5, bgcolor: "#eee", "& .MuiLinearProgress-bar": { bgcolor: "#000" } }} />
          <Typography variant="caption" color="text.secondary">{p.completion_percent}%</Typography>
        </Box>
      ),
    },
    {
      key: "actions", label: "", width: 60,
      render: (p) => (
        <ProjectRowActions project={p} onOpenBoard={openBoard} onManageAssignment={setDetailProject} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Projects"
        subtitle={projects ? `${projects.length} project(s)` : ""}
        action={<Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>New Project</Button>}
      />

      <DataTable columns={columns} rows={projects} loading={isLoading} emptyMessage="No projects yet" onRowClick={setDetailProject} />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
            <TextField label="Start date" type="date" InputLabelProps={{ shrink: true }} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            <TextField label="End date" type="date" InputLabelProps={{ shrink: true }} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!form.name || createMutation.isPending} onClick={() => createMutation.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <ProjectDetailDialog project={detailProject} onClose={() => setDetailProject(null)} />
    </Box>
  );
}

function ProjectRowActions({ project, onOpenBoard, onManageAssignment }) {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}>
        <MoreHorizontal size={18} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { setAnchorEl(null); onOpenBoard(project); }}>
          <Kanban size={14} style={{ marginRight: 8 }} /> Open Board
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); onManageAssignment(project); }}>
          <Users size={14} style={{ marginRight: 8 }} /> Manage Assignment
        </MenuItem>
      </Menu>
    </>
  );
}

// Members are stored on the project purely as employee ids -- assigning a
// whole Team is a shortcut that expands to the same thing (adds each of the
// team's current members individually), not a distinct relationship the
// backend tracks.
function ProjectDetailDialog({ project, onClose }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [employee, setEmployee] = useState(null);
  const [team, setTeam] = useState(null);

  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees, enabled: Boolean(project) });
  const { data: teams } = useQuery({ queryKey: ["teams"], queryFn: identityService.listTeams, enabled: Boolean(project) });

  const employeeById = useMemo(
    () => Object.fromEntries((employees || []).map((e) => [e.id, e])),
    [employees]
  );

  const addMemberMutation = useMutation({
    mutationFn: (empId) => projectService.addMember(project.id, empId),
    onSuccess: () => {
      enqueueSnackbar("Member added", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const addTeamMutation = useMutation({
    mutationFn: async (teamToAdd) => {
      const existing = project.member_employee_ids || [];
      const toAdd = (teamToAdd.member_employee_ids || []).filter((id) => !existing.includes(id));
      await Promise.all(toAdd.map((id) => projectService.addMember(project.id, id)));
      return toAdd.length;
    },
    onSuccess: (addedCount) => {
      enqueueSnackbar(
        addedCount ? `Added ${addedCount} team member(s)` : "That team's members are already on this project",
        { variant: addedCount ? "success" : "info" }
      );
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (empId) => projectService.removeMember(project.id, empId),
    onSuccess: () => {
      enqueueSnackbar("Member removed", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  return (
    <Dialog open={Boolean(project)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{project?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>Members</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {(project?.member_employee_ids || []).map((id) => (
            <Chip
              key={id}
              label={employeeById[id]?.name || `Employee #${id}`}
              onDelete={() => removeMemberMutation.mutate(id)}
              size="small"
            />
          ))}
          {(!project?.member_employee_ids || project.member_employee_ids.length === 0) && (
            <Typography variant="body2" color="text.secondary">No members yet</Typography>
          )}
        </Stack>

        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.5 }}>
          Assign by individual
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Autocomplete
            size="small" fullWidth
            options={employees || []}
            getOptionLabel={(e) => e.name || e.email}
            value={employee}
            onChange={(_, v) => setEmployee(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} placeholder="Search employees..." />}
          />
          <Button
            variant="outlined"
            disabled={!employee || addMemberMutation.isPending}
            onClick={() => { addMemberMutation.mutate(employee.id); setEmployee(null); }}
          >
            Add
          </Button>
        </Stack>

        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.5 }}>
          Assign by team
        </Typography>
        <Stack direction="row" spacing={1}>
          <Autocomplete
            size="small" fullWidth
            options={teams || []}
            getOptionLabel={(t) => t.name}
            value={team}
            onChange={(_, v) => setTeam(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} placeholder="Search teams..." />}
          />
          <Button
            variant="outlined"
            disabled={!team || addTeamMutation.isPending}
            onClick={() => { addTeamMutation.mutate(team); setTeam(null); }}
          >
            Add
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
